import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { ConfigService } from '@neworg-net/amsuite-core/config';
import { requireHostUrl } from '../shared/required-host-url';
import { httpOptions } from '../constants/http-options.constant';
import {
  CompanyTextSavedDto,
  CompanyTextVersionDto,
  CustomizableEventDto,
  EventTextsDto,
  NotificationDesignCapabilitiesDto,
  NotificationPreviewDto,
  NotificationPreviewRequest,
  SaveCompanyTextRequest,
} from '../models';

/**
 * Chiavi che il browser leggerebbe come segmenti `.` e `..` del percorso anche codificate, e la richiesta
 * andrebbe a un altro endpoint (`/events/../texts` è `/texts`). Nessun evento ha una chiave così.
 */
const DOT_SEGMENTS = new Set(['', '.', '..']);

/**
 * Client dei testi delle email personalizzati per azienda (Notifier, /notifier/v1/design, #2103).
 * Bearer, X-Party-Owner-Id e X-Impersonation-Id li mettono gli interceptor di amsuite-core su ogni
 * URL del Notifier: qui nessun header a mano. La chiave dell'evento, la lingua e la forma vanno nel
 * percorso codificate; una chiave che il percorso non può contenere vale come un evento inesistente (404).
 */
@Injectable({ providedIn: 'root' })
export class NotifierTemplatesService {
  private readonly _http = inject(HttpClient);
  private readonly _config = inject(ConfigService);
  private readonly _baseUrl = requireHostUrl(this._config, 'notifier') + '/notifier/v1/design';

  /** Se l'utente gestisce i testi dell'azienda corrente: decide la voce di menu e il guard. */
  getCapabilities(): Observable<NotificationDesignCapabilitiesDto> {
    return this._http.get<NotificationDesignCapabilitiesDto>(`${this._baseUrl}/capabilities`, httpOptions);
  }

  /** Gli eventi personalizzabili, con lo stato dei testi dell'azienda per lingua e forma. */
  getEvents(): Observable<CustomizableEventDto[]> {
    return this._http.get<CustomizableEventDto[]>(`${this._baseUrl}/events`, httpOptions);
  }

  /** I testi di suite e dell'azienda di un evento, per lingua e forma. */
  getTexts(templateKey: string): Observable<EventTextsDto> {
    return this.forEvent(templateKey, () =>
      this._http.get<EventTextsDto>(`${this.eventUrl(templateKey)}/texts`, httpOptions));
  }

  /** Salva e mette in vigore un testo: 409 se la versione in vigore non è più `expectedVersion`. */
  saveText(templateKey: string, locale: string, form: string, request: SaveCompanyTextRequest): Observable<CompanyTextSavedDto> {
    return this.forEvent(templateKey, () =>
      this._http.put<CompanyTextSavedDto>(this.textUrl(templateKey, locale, form), request, httpOptions));
  }

  /** Ritira il testo dell'azienda: torna quello di suite. */
  withdrawText(templateKey: string, locale: string, form: string, expectedVersion: number): Observable<void> {
    const params = new HttpParams().set('expectedVersion', expectedVersion);
    return this.forEvent(templateKey, () =>
      this._http.delete<void>(this.textUrl(templateKey, locale, form), { ...httpOptions, params }));
  }

  /** Lo storico di un testo, dalla versione più recente (al più 50). */
  getVersions(templateKey: string, locale: string, form: string): Observable<CompanyTextVersionDto[]> {
    return this.forEvent(templateKey, () =>
      this._http.get<CompanyTextVersionDto[]>(`${this.textUrl(templateKey, locale, form)}/versions`, httpOptions));
  }

  /** Rimette in vigore il testo di una versione dello storico, come versione nuova. */
  restoreVersion(
    templateKey: string, locale: string, form: string, version: number, expectedVersion: number | null,
  ): Observable<CompanyTextSavedDto> {
    return this.forEvent(templateKey, () => this._http.post<CompanyTextSavedDto>(
      `${this.textUrl(templateKey, locale, form)}/versions/${encodeURIComponent(version)}/restore`,
      { expectedVersion },
      httpOptions,
    ));
  }

  /** L'email come uscirebbe, con le bozze davanti ai testi salvati. Non invia e non scrive niente. */
  preview(request: NotificationPreviewRequest): Observable<NotificationPreviewDto> {
    return this._http.post<NotificationPreviewDto>(`${this._baseUrl}/preview`, request, httpOptions);
  }

  private forEvent<T>(templateKey: string, send: () => Observable<T>): Observable<T> {
    return DOT_SEGMENTS.has(templateKey)
      ? throwError(() => new HttpErrorResponse({ status: 404, statusText: 'Not Found' }))
      : send();
  }

  private eventUrl(templateKey: string): string {
    return `${this._baseUrl}/events/${encodeURIComponent(templateKey)}`;
  }

  private textUrl(templateKey: string, locale: string, form: string): string {
    return `${this.eventUrl(templateKey)}/texts/${encodeURIComponent(locale)}/${encodeURIComponent(form)}`;
  }
}
